import React, { useCallback } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { MsalProvider, useMsal } from "@azure/msal-react";
import { msalInstance } from "./auth/msalConfig";
import HomePage from "./pages/HomePage";
import AreaPage from "./pages/AreaPage";
import FooterNav from "./components/FooterNav";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import { Area } from "./models/Area";
import { AuthProvider } from "./context/AuthContext";
import { useAreasData } from "./hooks/useAreasData";
import { useRatingsSync } from "./hooks/useRatingsSync";
import { useCountdown } from "./hooks/useCountdown";
import AreasNav from "./components/AreasNav";

const loginRequest = {
  scopes: [process.env.REACT_APP_API_SCOPE!]
};

const isDevMode = process.env.REACT_APP_DEV_MODE === "true";

const AreasProvider: React.FC = () => {
  const { instance, accounts } = useMsal();
  
  // Auth data
  const isLoggedIn = isDevMode ? true : accounts.length > 0;
  const account = accounts[0];
  const userName = isDevMode
    ? process.env.REACT_APP_DEV_USER_NAME || "Dev User"
    : account?.name || null;
  // Extract entraObjectId (assuming it's stored in account.localAccountId or idTokenClaims.oid)
  const entraObjectId = isDevMode
    ? "dev-entra-object-id"
    : (account?.idTokenClaims as any)?.oid || account?.localAccountId;

  // Token getter
  const getAccessToken = useCallback(async () => {
    if (isDevMode) {
      return "dev-mock-token";
    }
    if (!account) throw new Error("No account");
    const resp = await instance.acquireTokenSilent({
      ...loginRequest,
      account
    });
    return resp.accessToken;
  }, [account, instance]);

  // Custom hooks for state management
  const {
    areas,
    ratings,
    setRatings,
    draftEntry,
    draftId,
    noActivePeriod,
    turnedIn,
    setTurnedIn,
    setNoActivePeriod,
  } = useAreasData({ isLoggedIn, getAccessToken });

  const { syncState, submitState, handleFullSync, handleSubmit } = useRatingsSync({
    isLoggedIn,
    areas,
    ratings,
    draftEntry,
    getAccessToken,
    onSubmitSuccess: () => {
      setTurnedIn(true);
      setNoActivePeriod(true);
    }
  });

  const countdown = useCountdown({
    endTime: draftEntry?.endTime,
    noActivePeriod,
    turnedIn
  });

  // Utility function
  const isAreaComplete = useCallback(
    (area: Area, r: Record<string, number>) =>
      (area.subArea ?? []).every(sa =>
        (sa.subAreaDetails ?? []).every(d => {
          const key = `${sa.subAreaId}-${d.subAreaDetailId}`;
          return typeof r[key] === "number" && r[key] > -1;
        })
      ),
    []
  );

  // Auth handlers
  const handleLogin = () => {
    instance.loginRedirect(loginRequest);
  };
  const handleLogout = () => {
    instance.logoutRedirect();
  };

  // Simple loading flag (optional)
  const loading =
    isLoggedIn &&
    !noActivePeriod &&
    !turnedIn &&
    areas.length === 0 &&
    !draftEntry;

  return (
    <div style={{ backgroundColor: '#F4F3EF', minHeight: '100vh' }}>
      {/* HEADER / NAV */}
      <AreasNav
        areas={areas}
        ratings={ratings}
        isAreaComplete={isAreaComplete}
        isLoggedIn={isLoggedIn}
        noActivePeriod={noActivePeriod}
      />

      {/* MAIN ROUTES */}
      <div style={{ paddingBottom: '70px' }}>
      <Routes>
        <Route
          path="/"
          element={
            loading ? (
              <div style={{ padding: 40 }}>Loading…</div>
            ) : (
              <HomePage
                areas={areas}
                ratings={ratings}
                isAreaComplete={isAreaComplete}
                noActivePeriod={noActivePeriod}
                turnedIn={turnedIn}
                isLoggedIn={isLoggedIn}
                userName={userName}
                draftEntry={draftEntry}
                syncState={syncState}
                submitState={submitState}
                countdown={countdown}
                onLogin={handleLogin}
                onLogout={handleLogout}
                onSync={handleFullSync}
                onSubmit={handleSubmit}
              />
            )
          }
        />
        <Route
          path="/areas/:areaId"
          element={
            loading ? (
              <div style={{ padding: 40 }}>Loading…</div>
            ) : (
              <AreaPage
                areas={areas}
                ratings={ratings}
                setRatings={setRatings}
                isAreaComplete={isAreaComplete}
                activeDraftId={draftId}
                isLoggedIn={isLoggedIn}
                noActivePeriod={noActivePeriod}
              />
            )
          }
        />
        <Route
          path="/admin"
          element={<AdminDashboardPage />}
        />
      </Routes>
      </div>

      {/* FOOTER */}
      <FooterNav
        areas={areas}
        isLoggedIn={isLoggedIn}
        noActivePeriod={noActivePeriod}
      />
    </div>
  );
};

function App() {
  return (
    <MsalProvider instance={msalInstance}>
      <BrowserRouter>
        <AuthProvider>
          <AreasProvider />
        </AuthProvider>
      </BrowserRouter>
    </MsalProvider>
  );
}

export default App;
